/**
 * v1.2 Batch 5-B：投放花费录入 + CSV 批量导入
 *
 * /investment/cost-import
 * - 单条录入：维度 + 维度值 + 时间段 + 花费 + 备注
 * - 批量 CSV：列顺序 dimension,dimension_value,period_type,period_start,period_end,spend_cny,notes
 * - 历史列表 + 编辑（带 audit）
 * - 5-E 数据源切换（admin）：当前 / 切换到「自动同步」按钮（会报 NotImplementedError）
 */
import { useState, useRef } from 'react';
import { Input, InputNumber, Select, DatePicker, Modal, message, Table } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';
import { useAuthStore } from '../../store/auth';

const DIMENSIONS = [
  { value: 'campaign', label: '投放计划' },
  { value: 'unit', label: '广告组' },
  { value: 'creativity', label: '创意' },
  { value: 'account', label: '投放账户' },
  { value: 'note', label: '归因笔记' },
  { value: 'author', label: '笔记作者' },
];

export function CostImportPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [dimension, setDimension] = useState('campaign');
  const [dimensionValue, setDimensionValue] = useState('');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([dayjs().subtract(7, 'day'), dayjs()]);
  const [spendCny, setSpendCny] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState<number | null>(null);
  const [editNotes, setEditNotes] = useState('');

  const { data: list, refetch } = useQuery<any>({
    queryKey: ['ad-spend-list'],
    queryFn: () => $.get('/reports/ad/spend', { pageSize: 50 }),
  });

  const { data: source } = useQuery<{ source: string }>({
    queryKey: ['ad-spend-source'],
    queryFn: () => $.get('/reports/ad/source'),
  });

  const create = useMutation({
    mutationFn: () => $.post('/reports/ad/spend', {
      dimension, dimensionValue,
      periodType: 'day',
      periodStart: range![0].format('YYYY-MM-DD'),
      periodEnd: range![1].format('YYYY-MM-DD'),
      spendCny: spendCny,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      message.success('已录入');
      setDimensionValue(''); setSpendCny(null); setNotes('');
      qc.invalidateQueries({ queryKey: ['ad-spend-list'] });
    },
    onError: (e: any) => message.error(e?.message ?? '录入失败'),
  });

  const update = useMutation({
    mutationFn: () => $.patch(`/reports/ad/spend/${editingId}`, {
      spendCny: editAmount, notes: editNotes,
    }),
    onSuccess: () => {
      message.success('已更新');
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['ad-spend-list'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => $.post(`/reports/ad/spend/${id}/delete`, {}),
    onSuccess: () => {
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['ad-spend-list'] });
    },
    // ux1：删除改 mutateAsync 拿 Modal.confirm 隐式 loading，失败需 toast 避免静默
    onError: (e: any) => message.error(e?.message ?? '删除失败'),
  });

  const batchUpload = useMutation({
    mutationFn: (rows: any[]) => $.post('/reports/ad/spend/batch-import', { rows }),
    onSuccess: (r: any) => {
      message.success(`导入完成：新增 ${r.created}，跳过 ${r.skipped}${r.errors?.length ? `（${r.errors.length} 条错误）` : ''}`);
      if (r.errors?.length) console.warn('CSV import errors:', r.errors);
      qc.invalidateQueries({ queryKey: ['ad-spend-list'] });
    },
    onError: (e: any) => message.error(e?.message ?? '导入失败'),
  });

  const setSource = useMutation({
    mutationFn: (s: 'manual' | 'xiaohongshu_api') =>
      $.post('/reports/ad/source', { source: s }),
    onSuccess: () => {
      message.success('已切换');
      qc.invalidateQueries({ queryKey: ['ad-spend-source'] });
    },
    onError: (e: any) => message.error(e?.message ?? '切换失败'),
  });

  const onPickFile = (e: any) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result ?? '');
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        message.error('CSV 至少要有表头 + 1 行数据');
        return;
      }
      const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map((line) => {
        const cells = parseCsvLine(line);
        const obj: any = {};
        headers.forEach((h, i) => { obj[h] = cells[i]; });
        return obj;
      });
      batchUpload.mutate(rows);
    };
    reader.readAsText(f, 'utf-8');
    if (fileRef.current) fileRef.current.value = '';
  };

  const isAdmin = me?.role === 'admin';

  return (
    <>
      <PageHeader
        breadcrumb={['投流营销', '投放花费录入']}
        title="投放花费录入"
        subtitle="任务 D 调查：小红书暂仅 webhook 被动接收，ROI 走手填路径。后续聚光 API 接入后切换自动同步。"
        actions={
          <>
            <Btn kind="default" onClick={() => fileRef.current?.click()}>
              CSV 批量导入
            </Btn>
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onPickFile} />
          </>
        }
      />
      <div className="work-content">
        {/* 5-E 数据源切换 */}
        <HomeCard title="数据源">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
            <div>
              当前：
              <Pill tone={source?.source === 'manual' ? 'primary' : 'success'}>
                {source?.source === 'manual' ? '手填录入' : '自动同步（小红书聚光）'}
              </Pill>
            </div>
            {isAdmin && (
              <>
                <Btn kind="default" disabled={source?.source === 'manual' || setSource.isPending}
                     onClick={() => setSource.mutate('manual')}>
                  切到手填
                </Btn>
                <Btn kind="default" disabled={source?.source === 'xiaohongshu_api' || setSource.isPending}
                     onClick={() => setSource.mutate('xiaohongshu_api')}>
                  切到自动同步
                </Btn>
                <span style={{ fontSize: 12, color: 'var(--text-3, #8F959E)' }}>
                  自动同步暂禁用（接口未实现，点会报错）
                </span>
              </>
            )}
          </div>
        </HomeCard>

        {/* 单条录入 */}
        <HomeCard title="单条录入">
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <Lbl>维度</Lbl>
              <Select value={dimension} onChange={setDimension} style={{ width: 140 }}
                      options={DIMENSIONS} />
            </div>
            <div>
              <Lbl>维度值</Lbl>
              <Input value={dimensionValue} onChange={(e) => setDimensionValue(e.target.value)}
                     style={{ width: 240 }} placeholder="例：宝妈兼职计划 03" />
            </div>
            <div>
              <Lbl>时间区间</Lbl>
              <DatePicker.RangePicker value={range as any} onChange={(v: any) => setRange(v)}
                                      allowClear={false} />
            </div>
            <div>
              <Lbl>花费（¥）</Lbl>
              <InputNumber value={spendCny} onChange={(v) => setSpendCny(v != null ? Number(v) : null)}
                           min={0} style={{ width: 140 }} />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Lbl>备注</Lbl>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="可选" />
            </div>
            <Btn kind="primary"
                 disabled={!dimensionValue.trim() || !range || spendCny == null || create.isPending}
                 onClick={() => create.mutate()}>
              {create.isPending ? '录入中…' : '录入'}
            </Btn>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginTop: 10 }}>
            CSV 列：dimension, dimension_value, period_type, period_start, period_end, spend_cny, notes
          </div>
        </HomeCard>

        <HomeCard title={`历史记录（${list?.total ?? 0}）`}>
          <TableWrap>
            <Table
              rowKey="id"
              dataSource={list?.items ?? []}
              pagination={{ pageSize: 50 }}
              size="middle"
              columns={[
                { title: '维度', dataIndex: 'dimension', width: 100,
                  render: (v: string) => DIMENSIONS.find((d) => d.value === v)?.label ?? v },
                { title: '维度值', dataIndex: 'dimensionValue', width: 200 },
                { title: '时间区间', width: 200,
                  render: (_v: any, r: any) => `${r.periodStart} → ${r.periodEnd}` },
                { title: '花费 ¥', dataIndex: 'spendCny', width: 120,
                  render: (v: any, r: any) => editingId === r.id ? (
                    <InputNumber value={editAmount} onChange={(v: any) => setEditAmount(Number(v ?? 0))} min={0} />
                  ) : `¥${Number(v).toLocaleString()}` },
                { title: '来源', dataIndex: 'source', width: 100,
                  render: (s: string) => <Pill tone={s === 'manual' ? 'primary' : 'success'}>{s === 'manual' ? '手填' : s}</Pill> },
                { title: '备注', dataIndex: 'notes', width: 200,
                  render: (v: any, r: any) => editingId === r.id ? (
                    <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                  ) : v },
                { title: '操作', width: 160,
                  render: (_v: any, r: any) => editingId === r.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn kind="primary" onClick={() => update.mutate()} disabled={update.isPending}>{update.isPending ? '保存中…' : '保存'}</Btn>
                      <Btn kind="default" onClick={() => setEditingId(null)}>取消</Btn>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn kind="text" onClick={() => {
                        setEditingId(r.id);
                        setEditAmount(Number(r.spendCny));
                        setEditNotes(r.notes ?? '');
                      }}>编辑</Btn>
                      <Btn kind="text" onClick={() => Modal.confirm({
                        title: '确认删除这条投放花费记录？',
                        // ux1：onOk 返回 mutateAsync Promise，AntD 自动给确认钮加 spinner 并防重复点击
                        onOk: () => remove.mutateAsync(r.id),
                      })}>删除</Btn>
                    </div>
                  ),
                },
              ]}
            />
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

function Lbl({ children }: { children: any }) {
  return <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginBottom: 4 }}>{children}</div>;
}

/** 简单 CSV 行解析（支持 "" 引号转义） */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQuotes = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}
